import BaseStore from './BaseStore';
import _ from 'lodash';
import rp from 'request-promise';
import config from '../config';
import { getToken } from '../components/Token';
import { getTenantHeaders } from '../components/TenantHeaders';

const defaultData = {
  patientId: null,
  settingProfileId: null,
  getup: 0,
  walkingAt: 0,
  walkingStart: '22:00',
  walkingEnd: '04:00',
  movement: 0,
  movementLevel: 3,
  notTurnFor: 1,
  notTurnForTime: 120,
  notTurnForHours: 2,
  notTurnForMinutes: 0,
  noMovementFor: 0,
  noMovementForTime: 2880,
  noMovementForDay: 2,
  fall: 1,
  intoArea: 0,
  outOfArea: 0,
  intoAreaRID: [],
  lowBattery: 1,
  noSignal: 0,
  intoBuilding: [],
  outOfBuilding: [],
};

export class SettingStore extends BaseStore {
  constructor() {
    super();
    this.observable({
      loading: false,
      data: _.cloneDeep(defaultData),
      profileData: {},
    });
  }

  changeData(key, value) {
    this.data[key] = value;
  }

  async settingCreate(patientId) {
    this.loading = true;
    try {
      let detail = defaultData;
      detail.intoAreaRID = JSON.stringify(detail.intoAreaRID);
      detail.intoBuilding = JSON.stringify(detail.intoBuilding);
      detail.outOfBuilding = JSON.stringify(detail.outOfBuilding);
      detail.patientId = patientId;
      const res = await rp
        .post(`${config.api.url}/${config.api.version}/setwarning`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res.body;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async settingUpdate(patient) {
    this.loading = true;
    try {
      let detail = this.data;
      const checkSetting = await this.checkSettingsData(+detail.settingProfileId);
      if (checkSetting) {
        detail.settingProfileId = +detail.settingProfileId;
      } else {
        detail.settingProfileId = null;
      }
      detail.notTurnForTime = +(+detail.notTurnForHours * 60 + +detail.notTurnForMinutes);
      detail.noMovementForTime = +detail.noMovementForDay * 1440;
      detail.getup = +detail.getup;
      detail.movement = +detail.movement;
      detail.movementLevel = +detail.movementLevel;
      detail.walkingAt = +detail.walkingAt;
      detail.notTurnFor = +detail.notTurnFor;
      detail.notTurnForTime = +detail.notTurnForTime;
      detail.notTurnForHours = +detail.notTurnForHours;
      detail.notTurnForMinutes = +detail.notTurnForMinutes;
      detail.noMovementFor = +detail.noMovementFor;
      detail.noMovementForTime = +detail.noMovementForTime;
      detail.noMovementForDay = +detail.noMovementForDay;
      detail.fall = +detail.fall;
      detail.intoArea = +detail.intoArea;
      detail.intoAreaRID = JSON.stringify(detail.intoAreaRID);
      detail.intoBuilding = JSON.stringify(detail.intoBuilding);
      detail.outOfBuilding = JSON.stringify(detail.outOfBuilding);
      detail.outOfArea = +detail.outOfArea;
      detail.lowBattery = +detail.lowBattery;
      detail.noSignal = +detail.noSignal;
      const res = await rp
        .put(`${config.api.url}/${config.api.version}/setwarning/${detail.id}`, {
          body: detail,
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        if (patient) {
          await rp
            .put(`${config.api.url}/${config.api.version}/notification/update/${patient.id}`, {
              body: detail,
              json: true,
              authorization: true,
              resolveWithFullResponse: true,
              headers: {
                ...getTenantHeaders(),
              },
            })
            .auth(null, null, true, getToken('id_token'));

          const bedResponse = await rp
            .get(`${config.api.url}/${config.api.version}/bed/patient/${patient.id}`, {
              authorization: true,
              resolveWithFullResponse: true,
              json: true,
              headers: {
                ...getTenantHeaders(),
              },
            })
            .auth(null, null, true, getToken('id_token'));
          if (bedResponse && bedResponse.body && bedResponse.body.subGroupId) {
            await rp
              .post(`${config.api.url}/${config.api.version}/fetch`, {
                body: { subGroupId: bedResponse.body.subGroupId, type: 'patientUpdate' },
                json: true,
                authorization: true,
                resolveWithFullResponse: true,
                headers: {
                  ...getTenantHeaders(),
                },
              })
              .auth(null, null, true, getToken('id_token'));
          }
        }
      }
    } catch (err) {
      return false;
    } finally {
      this.loading = false;
    }
  }

  async getSettingByPatient(id) {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/setwarning/${id}`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        let setting = res.body || {};
        let hours = Math.floor(setting.notTurnForTime / 60);
        let minutes = setting.notTurnForTime % 60;
        let day = Math.floor(setting.noMovementForTime / 1440);
        setting.notTurnForHours = hours;
        setting.notTurnForMinutes = minutes;
        setting.noMovementForDay = day;
        try {
          // setting.intoAreaRID = JSON.parse(setting.intoAreaRID)
          setting.intoBuilding = JSON.parse(setting.intoBuilding);
          setting.outOfBuilding = JSON.parse(setting.outOfBuilding);
        } catch (e) {
          // setting.intoAreaRID = [];
          setting.intoBuilding = [];
          setting.outOfBuilding = [];
        }
        this.data = setting;
        this.profileData = setting;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async getDefaultWarningSettings() {
    this.loading = true;
    try {
      let url = `${config.api.url}/${config.api.version}/setwarning/`;
      const res = await rp
        .get(url, {
          authorization: true,
          resolveWithFullResponse: true,
          json: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res.statusCode === 200) {
        let setting = res.body || {};
        let settingData = this.data;
        settingData.settingProfileId = setting.settingProfileId;
        settingData.getup = setting.getup;
        settingData.walkingAt = setting.walkingAt;
        settingData.walkingStart = setting.walkingStart;
        settingData.walkingEnd = setting.walkingEnd;
        settingData.movement = setting.movement;
        settingData.movementLevel = setting.movementLevel;
        settingData.notTurnFor = setting.notTurnFor;
        settingData.notTurnForTime = setting.notTurnForTime;
        settingData.noMovementFor = setting.noMovementFor;
        settingData.noMovementForTime = setting.noMovementForTime;
        settingData.fall = setting.fall;
        settingData.intoArea = setting.intoArea;
        settingData.outOfArea = setting.outOfArea;
        settingData.lowBattery = setting.lowBattery;
        settingData.noSignal = setting.noSignal;

        let hours = Math.floor(setting.notTurnForTime / 60);
        let minutes = setting.notTurnForTime % 60;
        let day = Math.floor(setting.noMovementForTime / 1440);
        settingData.notTurnForHours = hours;
        settingData.notTurnForMinutes = minutes;
        settingData.noMovementForDay = day;
        try {
          // settingData.intoAreaRID = JSON.parse(setting.intoAreaRID);
          settingData.intoBuilding = JSON.parse(setting.intoBuilding);
          settingData.outOfBuilding = JSON.parse(setting.outOfBuilding);
        } catch (e) {
          // settingData.intoAreaRID = [];
          settingData.intoBuilding = [];
          settingData.outOfBuilding = [];
        }
        this.data = settingData;
        this.profileData = settingData;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async settingDelete(patientId) {
    this.loading = true;
    try {
      const res = await rp
        .delete(`${config.api.url}/${config.api.version}/setwarning/patient/${patientId}`, {
          body: {},
          json: true,
          authorization: true,
          resolveWithFullResponse: true,
          headers: {
            ...getTenantHeaders(),
          },
        })
        .auth(null, null, true, getToken('id_token'));
      if (res && res.statusCode === 200) {
        return res.body;
      }
    } catch (err) {
      throw err;
    } finally {
      this.loading = false;
    }
  }

  async setWarningSettings(data) {
    let settingData = _.cloneDeep(this.data);
    if (data) {
      settingData.settingProfileId = data.id;
      settingData.getup = data.getup;
      settingData.walkingAt = data.walkingAt;
      settingData.walkingStart = data.walkingStart;
      settingData.walkingEnd = data.walkingEnd;
      settingData.movement = data.movement;
      settingData.movementLevel = data.movementLevel;
      settingData.notTurnFor = data.notTurnFor;
      settingData.notTurnForTime = data.notTurnForTime;
      settingData.noMovementFor = data.noMovementFor;
      settingData.noMovementForTime = data.noMovementForTime;
      settingData.fall = data.fall;
      settingData.intoArea = data.intoArea;
      settingData.outOfArea = data.outOfArea;
      settingData.lowBattery = data.lowBattery;
      settingData.noSignal = data.noSignal;

      let hours = Math.floor(data.notTurnForTime / 60);
      let minutes = data.notTurnForTime % 60;
      let day = Math.floor(data.noMovementForTime / 1440);
      settingData.notTurnForHours = hours;
      settingData.notTurnForMinutes = minutes;
      settingData.noMovementForDay = day;
      try {
        // settingData.intoAreaRID = JSON.parse(data.intoAreaRID);
        settingData.intoBuilding = JSON.parse(data.intoBuilding);
        settingData.outOfBuilding = JSON.parse(data.outOfBuilding);
      } catch (e) {
        // settingData.intoAreaRID = [];
        settingData.intoBuilding = [];
        settingData.outOfBuilding = [];
      }
    } else {
      let defaultSetting = _.cloneDeep(defaultData);
      settingData.settingProfileId = defaultSetting.settingProfileId;
      settingData.getup = defaultSetting.getup;
      settingData.walkingAt = defaultSetting.walkingAt;
      settingData.walkingStart = defaultSetting.walkingStart;
      settingData.walkingEnd = defaultSetting.walkingEnd;
      settingData.movement = defaultSetting.movement;
      settingData.movementLevel = defaultSetting.movementLevel;
      settingData.notTurnFor = defaultSetting.notTurnFor;
      settingData.notTurnForTime = defaultSetting.notTurnForTime;
      settingData.noMovementFor = defaultSetting.noMovementFor;
      settingData.noMovementForTime = defaultSetting.noMovementForTime;
      settingData.fall = defaultSetting.fall;
      settingData.intoArea = defaultSetting.intoArea;
      settingData.outOfArea = defaultSetting.outOfArea;
      settingData.lowBattery = defaultSetting.lowBattery;
      settingData.noSignal = defaultSetting.noSignal;
      settingData.intoAreaRID = defaultSetting.intoAreaRID;
      settingData.intoBuilding = defaultSetting.intoBuilding;
      settingData.outOfBuilding = defaultSetting.outOfBuilding;

      let hours = Math.floor(defaultSetting.notTurnForTime / 60);
      let minutes = defaultSetting.notTurnForTime % 60;
      let day = Math.floor(defaultSetting.noMovementForTime / 1440);
      settingData.notTurnForHours = hours;
      settingData.notTurnForMinutes = minutes;
      settingData.noMovementForDay = day;
    }
    this.profileData = settingData;
    this.data = settingData;
  }

  async checkSettingsData(id) {
    const settingData = this.data;
    const profileData = this.profileData;
    const walkingStartSetting = settingData.walkingStart.slice(0, 5);
    const walkingStartProfile = profileData.walkingStart.slice(0, 5);
    const walkingEndSetting = settingData.walkingEnd.slice(0, 5);
    const walkingEndProfile = profileData.walkingEnd.slice(0, 5);

    if (id) {
      if (!_.isEqual(settingData.intoAreaRID, profileData.intoAreaRID)) {
        return false;
      }
      if (!_.isEqual(settingData.intoBuilding, profileData.intoBuilding)) {
        return false;
      }
      if (!_.isEqual(settingData.outOfBuilding, profileData.outOfBuilding)) {
        return false;
      }
      if (+settingData.getup !== +profileData.getup) {
        return false;
      } else if (+settingData.walkingAt !== +profileData.walkingAt) {
        return false;
      } else if (walkingStartSetting !== walkingStartProfile) {
        return false;
      } else if (walkingEndSetting !== walkingEndProfile) {
        return false;
      } else if (+settingData.movement !== +profileData.movement) {
        return false;
      } else if (+settingData.movementLevel !== +profileData.movementLevel) {
        return false;
      } else if (+settingData.notTurnFor !== +profileData.notTurnFor) {
        return false;
      } else if (+settingData.notTurnForTime !== +profileData.notTurnForTime) {
        return false;
      } else if (+settingData.noMovementFor !== +profileData.noMovementFor) {
        return false;
      } else if (+settingData.noMovementForTime !== +profileData.noMovementForTime) {
        return false;
      } else if (+settingData.fall !== +profileData.fall) {
        return false;
      } else if (+settingData.intoArea !== +profileData.intoArea) {
        return false;
      } else if (+settingData.outOfArea !== +profileData.outOfArea) {
        return false;
      } else if (+settingData.lowBattery !== +profileData.lowBattery) {
        return false;
      } else if (+settingData.noSignal !== +profileData.noSignal) {
        return false;
      } else if (+settingData.notTurnForMinutes !== +profileData.notTurnForMinutes) {
        return false;
      } else if (+settingData.notTurnForHours !== +profileData.notTurnForHours) {
        return false;
      } else if (+settingData.noMovementForDay !== +profileData.noMovementForDay) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }
}
export default new SettingStore();
